import {NavLink, useRouteMatch} from "react-router-dom";
import {RouterViewTemplate, useLoadingState} from "../RouterView";
import {getContainer} from "../../ioc/IOCSetup";
import {AdminScriptWidget, UpdateScriptWidget} from "../../http/protocol";
import {Form, useForm} from "../form/Form";
import {FormGroup} from "../form/FormGroup";
import {FormInput} from "../form/FormInput";
import {FormSubmitError, FormSubmitSuccess} from "../form/FormFeedback";
import {Submit} from "../form/Submit";
import {Validators} from "../form/Validators";
import {t} from "../../i18n";


const MUTABLE_FIELDS: (keyof UpdateScriptWidget)[] = [
    "name", "description", "scriptUrl", "variableName",
    "integrationUrl", "integrationApiKey", "enabled"
];

export const EditWidget = () => {
    const match = useRouteMatch<{ id: string }>();
    const isNew = match.params.id === "new";

    const [form, listener, setFormState] = useForm<AdminScriptWidget>({}, () => {
        const service = getContainer().getScriptWidgetService();
        if (isNew) {
            return service.save(form.model).then(r => setFormState({model: r, validationMap: form.validationMap}));
        }
        const updateDto: Partial<UpdateScriptWidget> = {};
        for (const field of MUTABLE_FIELDS) {
            if (form.validationMap[field] && !form.validationMap[field].pristine) {
                (updateDto as any)[field] = form.model[field];
            }
        }
        return service.updateWidget(form.model.id!, updateDto).then(r => setFormState({model: r, validationMap: form.validationMap}));
    });

    const status = useLoadingState(() => {
        const promise = isNew ? Promise.resolve({} as AdminScriptWidget) : getContainer().getScriptWidgetService().getEntity(match.params.id);
        return promise.then((w: AdminScriptWidget) => {
            setFormState({model: w, validationMap: {}});
        })
    }, [])

    return (
        <RouterViewTemplate status={status} title={t("widget.editTitle")} path={"/widget/" + (form.model.id ? form.model.id : "new")}>
            <div style={{margin: "10px 0"}}>
                <NavLink to={"/widgets"}>{t("widget.backToList")}</NavLink>
            </div>
            <div className={"card-box"}>

                <Form className={"form"} formListener={listener} modelObject={form.model} validationMap={form.validationMap}>
                    {!isNew && (
                        <FormGroup className={"form-group"}>
                            <label>{t("widget.labelId")}</label>
                            <FormInput className={"form-control"} model={"id"} disabled={true}/>
                        </FormGroup>
                    )}
                    <FormGroup className={"form-group"}>
                        <label>{t("widget.labelNameId")}</label>
                        {isNew ? (
                            <FormInput className={"form-control"} model={"nameId"} required={true}
                                       validators={[Validators.textId()]}/>
                        ) : (
                            <input className={"form-control"} value={form.model.nameId || ""} disabled={true}/>
                        )}
                        <span className={"help-block"}>{t("widget.helpNameId")}</span>
                    </FormGroup>
                    <FormGroup className={"form-group"}>
                        <label>{t("widget.labelName")}</label>
                        <FormInput className={"form-control"} model={"name"}/>
                        <span className={"help-block"}>{t("widget.helpName")}</span>
                    </FormGroup>
                    <FormGroup className={"form-group"}>
                        <label>{t("widget.labelDescription")}</label>
                        <FormInput className={"form-control"} model={"description"} type={"textarea"}/>
                        <span className={"help-block"}>{t("widget.helpDescription")}</span>
                    </FormGroup>
                    <FormGroup className={"form-group"}>
                        <label>{t("widget.labelVariableName")}</label>
                        <FormInput className={"form-control"} model={"variableName"} required={true}/>
                        <span className={"help-block"}>{t("widget.helpVariableName")}</span>
                    </FormGroup>
                    <FormGroup className={"form-group"}>
                        <label>{t("widget.labelScriptUrl")}</label>
                        <FormInput className={"form-control"} model={"scriptUrl"} required={true} data-ai-exclude="config"/>
                    </FormGroup>
                    <FormGroup className={"form-group"}>
                        <label>{t("widget.labelIntegrationUrl")}</label>
                        <FormInput className={"form-control"} model={"integrationUrl"} data-ai-exclude="config"/>
                        <span className={"help-block"}>{t("widget.helpIntegrationUrl")}</span>
                    </FormGroup>
                    <FormGroup className={"form-group"}>
                        <label>{t("widget.labelIntegrationApiKey")}</label>
                        <FormInput className={"form-control"} model={"integrationApiKey"} data-ai-exclude="secret"/>
                        <span className={"help-block"}>{t("widget.helpIntegrationApiKey")}</span>
                    </FormGroup>
                    <FormGroup className={"form-group"}>
                        <FormInput id={"enabled"} model={"enabled"} type={"checkbox"}/> <label htmlFor={"enabled"}>{t("widget.labelEnabled")}</label>
                    </FormGroup>

                    <FormSubmitError/>
                    <FormSubmitSuccess text={t("widget.savedSuccessfully")}/>
                    <Submit className={"btn btn-primary"}>{t("widget.submit")}</Submit>
                </Form>
            </div>
        </RouterViewTemplate>
    )
};